'use client'
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid2 from "@mui/material/Grid2";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import { CircularProgress, Box } from "@mui/material";
import { sendPasswordResetEmail } from "firebase/auth";

import { validateEmail } from "utils/helperFunctions";
import { auth } from "config/firebase";
import { getAuthErrorMessage, validateAuthInput } from "utils/authErrorMessages";

interface ResetPasswordProps {
  setSignin?: () => void;
  setSignup?: () => void;
}

export default function ResetPassword(props: ResetPasswordProps) {
  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [values, setValues] = useState({
    email: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const sendResetEmail = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMsg("");

    // Validate input before attempting password reset
    const validationErrors = validateAuthInput({
      email: values.email,
    });

    if (Object.keys(validationErrors).length > 0) {
      const firstError = Object.values(validationErrors)[0];
      setErrMsg(firstError);
      setIsLoading(false);
      return;
    }

    setErrMsg("");
    
    sendPasswordResetEmail(auth, values.email, {
      url: "https://localpoker.info/",
    })
      .then(() => {
        setSuccessMsg(
          "We have sent an email with instruction on how to reset your password.",
        );
      })
      .catch((err: any) => {
        console.error("Error resetting password:", err);
        const userFriendlyMessage = getAuthErrorMessage(err);
        setErrMsg(userFriendlyMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Avatar
        sx={{
          mt: 3,
          mb: 2,
          backgroundColor: "primary.main",
        }}
      >
        <LockOutlinedIcon />
      </Avatar>
      <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
        Reset Password
      </Typography>
      <Box component="form" noValidate sx={{ width: "100%" }}>
        <TextField
          value={values.email}
          onChange={handleInputChange}
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
        />
        {!isLoading ? (
          <Button
            onClick={sendResetEmail}
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
          >
            Reset Password
          </Button>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 3,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              This may take a moment...
            </Typography>
            <CircularProgress />
          </Box>
        )}
        {errMsg && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 2, textAlign: "center" }}
          >
            {errMsg}
          </Typography>
        )}
        {successMsg && (
          <Typography
            variant="body2"
            color="success"
            sx={{ mt: 2, textAlign: "center" }}
          >
            {successMsg}
          </Typography>
        )}
        <Grid2 container spacing={2} sx={{ mt: 2, justifyContent: "center" }}>
          <Grid2>
            <Link 
              component={RouterLink} 
              to="/signin" 
              variant="body2"
              onClick={props.setSignin}
            >
              Login
            </Link>
          </Grid2>
          <Grid2>
            <Link 
              component={RouterLink} 
              to="/signup" 
              variant="body2"
              onClick={props.setSignup}
            >
              Sign Up
            </Link>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
}
